class Motion {
    constructor() {
        this.initialized = false;
        this.init();
    }

    async init() {
        // Esperar a que GSAP y todos sus plugins estén disponibles
        await this.waitForGSAP();
        this.initialized = true;
        
        this.setupScrollSmoother();
        this.setupScrollAnimations();
    }

    waitForGSAP() {
        return new Promise((resolve) => {
            const checkGSAP = () => {
                if (gsap && window.ScrollTrigger && window.ScrollSmoother) {
                    // Registrar plugins de GSAP
                    gsap.registerPlugin(window.ScrollTrigger, window.ScrollSmoother);
                    resolve();
                } else {
                    // Reintentar después de un breve delay
                    setTimeout(checkGSAP, 50);
                }
            };
            checkGSAP();
        });
    }

    setupScrollSmoother() {
        if (window.ScrollSmoother) {
            window.ScrollSmoother.create({
                smooth: 1, // how long (in seconds) it takes to "catch up" to the native scroll position
                effects: true, // looks for data-speed and data-lag attributes on elements
                smoothTouch: 0.1, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
            })
        }
    }

    setupScrollAnimations() {
        if (!this.initialized || !gsap || !window.ScrollTrigger) return;
        
        // Animación fade-in para todas las secciones
        gsap.utils.toArray(".motion-section").forEach((section, index) => {
            // Configuración inicial para las secciones
            gsap.set(section, {
                opacity: 0,
                y: 50
            })

            // Animación de entrada
            gsap.to(section, {
                duration: 1,
                ease: "power2.out",
                opacity: 1,
                y: 0,
                scrollTrigger: {
                    trigger: section,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse"
                }
            })
        })

        // Animaciones específicas para diferentes tipos de sección
        this.setupSpecificAnimations()
    }

    setupSpecificAnimations() {
        // Animación slide desde la izquierda para secciones con clase .slide-left
        gsap.utils.toArray(".slide-left").forEach(section => {
            gsap.set(section, {
                x: -100,
                opacity: 0
            })

            gsap.to(section, {
                x: 0,
                opacity: 1,
                duration: 1.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: section,
                    start: "top 85%",
                    toggleActions: "play none none reverse"
                }
            })
        })

        // Animación slide desde la derecha para secciones con clase .slide-right
        gsap.utils.toArray(".slide-right").forEach(section => {
            gsap.set(section, {
                x: 100,
                opacity: 0
            })

            gsap.to(section, {
                x: 0,
                opacity: 1,
                duration: 1.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: section,
                    start: "top 85%",
                    toggleActions: "play none none reverse"
                }
            })
        })

        // Animación scale para secciones con clase .scale-in
        gsap.utils.toArray(".scale-in").forEach(section => {
            gsap.set(section, {
                scale: 0.75,
                opacity: 0,
                y: 100
            })

            gsap.to(section, {
                scale: 1,
                opacity: 1,
                duration: 1,
                y: 0,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: section,
                    start: "top 80%",
                    toggleActions: "play none none reverse"
                }
            })
        })

        // Animación rotación para secciones con clase .rotate-in
        gsap.utils.toArray(".rotate-in").forEach(section => {
            gsap.set(section, {
                rotation: 5,
                opacity: 0,
                y: 30
            })

            gsap.to(section, {
                rotation: 0,
                opacity: 1,
                y: 0,
                duration: .65,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: section,
                    start: "top 85%",
                    toggleActions: "play none none reverse"
                }
            })
        })

        // Animación stagger para elementos hijos en secciones con clase .stagger
        gsap.utils.toArray(".stagger").forEach(section => {
            const children = section.children
            
            gsap.set(children, {
                opacity: 0,
                y: 30
            })

            gsap.to(children, {
                opacity: 1,
                y: 0,
                duration: 0.45,
                stagger: 0.15,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: section,
                    start: "top 80%",
                    toggleActions: "play none none reverse"
                }
            })
        })
        
        gsap.utils.toArray(".move-up").forEach(section => {
            
            gsap.set(section, {
                opacity: 0,
                y: 30
            })

            gsap.to(section, {
                opacity: 1,
                y: 0,
                duration: 0.45,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: section,
                    start: "top 80%",
                    toggleActions: "play none none reverse"
                }
            })
        })
    }

    // Método para agregar animación personalizada a un elemento específico
    addCustomAnimation(element, options = {}) {
        if (!this.initialized || !gsap || !window.ScrollTrigger) return null;
        
        const defaultOptions = {
            opacity: 0,
            y: 50,
            duration: 1,
            ease: "power2.out",
            start: "top 80%"
        }

        const animationOptions = { ...defaultOptions, ...options }
        
        gsap.set(element, {
            opacity: animationOptions.opacity,
            y: animationOptions.y
        })

        return gsap.to(element, {
            opacity: 1,
            y: 0,
            duration: animationOptions.duration,
            ease: animationOptions.ease,
            scrollTrigger: {
                trigger: element,
                start: animationOptions.start,
                toggleActions: "play none none reverse"
            }
        })
    }

    // Método para refrescar ScrollTrigger (útil después de cambios dinámicos)
    refresh() {
        ScrollTrigger.refresh()
    }

    // Método para destruir todas las instancias de ScrollTrigger
    destroy() {
        ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    }
}

export default Motion
